/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API Analytics Summary Cards (Task 10.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  Activity,
  CheckCircle2,
  Clock,
  Zap,
  TrendingUp,
} from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { useDeveloperAnalyticsStore } from '../../../stores/useDeveloperAnalyticsStore';

export const AnalyticsSummaryCards: React.FC = () => {
  const { t } = useTranslation();
  const { getSummaryMetrics } = useDeveloperAnalyticsStore();

  const metrics = getSummaryMetrics();

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {/* 1. Total Requests */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between">
        <div className="space-y-1">
          <span className="text-xs text-foreground-muted font-medium">
            {t('apikeys.kpiTotalRequests')}
          </span>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold font-heading text-foreground">
              {metrics.totalRequests.toLocaleString()}
            </p>
            <span className="text-[11px] text-emerald-500 font-semibold inline-flex items-center">
              <TrendingUp className="size-3 mr-0.5" />
              +14%
            </span>
          </div>
        </div>
        <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Activity className="size-5" />
        </div>
      </div>

      {/* 2. Success Rate */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between">
        <div className="space-y-1">
          <span className="text-xs text-foreground-muted font-medium">
            {t('apikeys.kpiSuccessRate')}
          </span>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold font-heading text-emerald-500">
              {metrics.successRate}%
            </p>
            <span className="text-[11px] text-foreground-muted">
              p99 SLA
            </span>
          </div>
        </div>
        <div className="size-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
          <CheckCircle2 className="size-5" />
        </div>
      </div>

      {/* 3. Average Latency */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between">
        <div className="space-y-1">
          <span className="text-xs text-foreground-muted font-medium">
            {t('apikeys.kpiAvgLatency')}
          </span>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold font-heading text-foreground">
              {metrics.avgLatencyMs} <span className="text-xs font-normal text-foreground-muted">ms</span>
            </p>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 font-mono font-semibold">
              Edge KV
            </span>
          </div>
        </div>
        <div className="size-10 rounded-xl bg-accent/15 text-accent flex items-center justify-center">
          <Clock className="size-5" />
        </div>
      </div>

      {/* 4. Total Tokens & Credits */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between">
        <div className="space-y-1">
          <span className="text-xs text-foreground-muted font-medium">
            {t('apikeys.kpiTotalTokens')}
          </span>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold font-heading text-primary">
              {(metrics.totalTokens / 1000).toFixed(1)}k
            </p>
            <span className="text-[11px] text-foreground-muted">
              tokens
            </span>
          </div>
        </div>
        <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Zap className="size-5" />
        </div>
      </div>
    </div>
  );
};
